import { PlayFilledIcon } from '@jouzen/ecom-components/icons/PlayFilledIcon';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import type { MotionProps } from 'framer-motion';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import BackdropBlur from '@/app/components/BackdropBlur';
import Motion from '@/app/components/Motion';

export interface PopAccuracyComfortSectionVideoCtaProps {
  readonly introTransitionDelay: number;
  readonly isSectionVisible: boolean;
  readonly label: MessageKey;
  readonly onClick: () => void;
}

export const getMotionProps = ({
  introTransitionDelay,
  isSectionVisible,
}: {
  introTransitionDelay: number;
  isSectionVisible: boolean;
}): MotionProps => ({
  animate: {
    opacity: isSectionVisible ? 1 : 0,
  },
  initial: {
    opacity: 0,
  },
  transition: {
    delay: isSectionVisible ? introTransitionDelay : 0,
    duration: 0.75,
    type: 'tween',
  },
});

const PopAccuracyComfortSectionVideoCta = ({
  introTransitionDelay,
  isSectionVisible,
  label,
  onClick,
}: PopAccuracyComfortSectionVideoCtaProps) => {
  const t = useTranslations();
  const motionProps: MotionProps = getMotionProps({
    introTransitionDelay,
    isSectionVisible,
  });

  return (
    <Motion {...motionProps}>
      <button
        className="relative inline-flex items-center gap-x-2 overflow-hidden rounded-full px-5 py-3 text-sandstone-200 max-lg:bg-sandstone-500 md:mb-10"
        data-cy="pop-accuracy-comfort-section-video-cta"
        onClick={onClick}
        type="button"
      >
        <BackdropBlur
          className="max-lg:hidden"
          color={{ sm: 'dark/40', lg: 'dark/40' }}
          variant="pill"
        />
        <span className="relative flex size-8 items-center justify-center rounded-full bg-sandstone-200 text-sandstone-500">
          <PlayFilledIcon height={9} width={9} />
        </span>
        <Typography
          className="relative whitespace-nowrap text-current"
          Element="span"
          weight="bold"
        >
          {t(label)}
        </Typography>
      </button>
    </Motion>
  );
};

export default PopAccuracyComfortSectionVideoCta;
